<template>
  <div class="page">
    <header class="header">
      <button class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </button>
      <h1 class="title">扫码上架</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <div v-if="loading" class="status">加载中...</div>
      <div v-else-if="errorMsg" class="status">{{ errorMsg }}</div>
      <div v-else-if="!pallets.length" class="status">暂无托盘信息</div>

      <section v-else v-for="(p, idx) in pallets" :key="p.palletId || p.palletCode || idx" class="section">
        <div class="row">
          <div class="label">托盘码</div>
          <div class="value">{{ p.palletCode || '-' }}</div>
        </div>
        <div class="divider"></div>
        <button class="row row-btn" type="button" @click="openLocationSelector(idx)">
          <div class="label">库位信息</div>
          <div class="value value-placeholder" v-if="!p.selectedLocationText">请选择仓库</div>
          <div class="value" v-else>{{ p.selectedLocationText }}</div>
          <div class="arrow">›</div>
        </button>
      </section>
    </main>

    <div class="bottom-bar">
      <button class="bottom-btn" type="button" :disabled="!canSubmit || submitting" @click="handleSubmitAll">
        全部上架
      </button>
    </div>

    <div v-if="submitting" class="action-overlay">
      <div class="action-overlay-panel">
        <div class="action-spinner"></div>
        <div class="action-overlay-text">提交中...</div>
      </div>
    </div>

    <div v-if="showSelector" class="selector">
      <div class="selector-overlay" @click="closeSelector"></div>
      <div class="selector-panel">
        <header class="selector-header">
          <button class="selector-back" @click="closeSelector" aria-label="返回">
            <img src="@/assets/返回.svg" alt="返回" class="selector-back-icon" />
          </button>
          <div class="selector-title">选择库位</div>
          <div class="selector-right"></div>
        </header>

        <div class="selector-warehouse">{{ warehouseName || '-' }}</div>

        <div v-if="selectorLoading" class="selector-loading">加载中...</div>
        <div v-else-if="selectorError" class="selector-error">{{ selectorError }}</div>

        <div v-else class="selector-body">
          <aside class="areas">
            <button
              v-for="a in areas"
              :key="a.id"
              type="button"
              class="area-item"
              :class="{ active: activeAreaId === a.id }"
              @click="selectArea(a)"
            >
              {{ a.wareName || a.wareNo || '-' }}
            </button>
          </aside>

          <section class="locations">
            <div class="locations-title">{{ activeAreaName }}</div>
            <div class="location-grid">
              <button
                v-for="l in activeLocations"
                :key="l.id"
                type="button"
                class="location-card"
                :class="{ selected: selectedLocationId === l.id }"
                @click="selectLocation(l)"
              >
                <div class="location-name">{{ l.wareName || l.wareNo || '-' }}</div>
                <div class="location-sub">剩余托盘位：{{ l.remainPallet ?? '-' }}</div>
              </button>
            </div>
          </section>
        </div>

        <div class="selector-footer">
          <button class="selector-confirm" type="button" :disabled="!selectedLocationId" @click="confirmSelector">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import toast from '@/utils/toast'
import { getWareInPutawayDetail, getWareListByParentId, unloadWareIn } from '@/api/wareInPutaway'

export default {
  name: 'ScanPutaway',
  setup() {
    const route = useRoute()
    const router = useRouter()

    const storeId = ref('')
    const supplierId = ref('')
    const wareInId = ref('')

    const loading = ref(false)
    const errorMsg = ref('')
    const pallets = ref([])

    const submitting = ref(false)

    const showSelector = ref(false)
    const selectorLoading = ref(false)
    const selectorError = ref('')

    const warehouseName = ref('')
    const areas = ref([])
    const activeAreaId = ref('')
    const activeAreaName = ref('')

    const selectedArea = ref(null)
    const selectedLocation = ref(null)

    const selectedLocationId = computed(() => selectedLocation.value?.id || '')

    const activeLocations = computed(() => {
      const area = areas.value.find((a) => a.id === activeAreaId.value)
      return Array.isArray(area?.wareList) ? area.wareList : []
    })

    const canSubmit = computed(() => {
      return (
        Boolean(wareInId.value) &&
        Array.isArray(pallets.value) &&
        pallets.value.length > 0 &&
        pallets.value.every((p) => p.palletId && p.palletCode && p.selectedLocation?.id)
      )
    })

    const handleBack = () => router.back()

    const activePalletIndex = ref(-1)

    const openLocationSelector = async (index) => {
      activePalletIndex.value = index
      if (!storeId.value || !supplierId.value) {
        toast.error('缺少仓库或供应商信息')
        return
      }

      showSelector.value = true
      selectorError.value = ''

      const current = pallets.value?.[index]

      if (areas.value.length) {
        selectedArea.value = current?.selectedArea || null
        selectedLocation.value = current?.selectedLocation || null
        if (selectedArea.value?.id) {
          activeAreaId.value = selectedArea.value.id
          activeAreaName.value = selectedArea.value?.wareName || selectedArea.value?.wareNo || ''
        }
        return
      }

      selectorLoading.value = true
      try {
        const res = await getWareListByParentId({ id: storeId.value, supplierId: supplierId.value })
        const data = res?.data
        warehouseName.value = data?.wareName || ''
        const nextAreas = Array.isArray(data?.wareList) ? data.wareList : []
        areas.value = nextAreas
        const first = nextAreas[0]
        selectedArea.value = current?.selectedArea || null
        selectedLocation.value = current?.selectedLocation || null

        const initialAreaId = selectedArea.value?.id || first?.id || ''
        activeAreaId.value = initialAreaId
        const initialArea = nextAreas.find((a) => a.id === initialAreaId) || first
        activeAreaName.value = initialArea?.wareName || initialArea?.wareNo || ''
      } catch (e) {
        selectorError.value = e?.message || '加载失败'
      } finally {
        selectorLoading.value = false
      }
    }

    const closeSelector = () => {
      showSelector.value = false
      activePalletIndex.value = -1
    }

    const selectArea = (area) => {
      activeAreaId.value = area?.id || ''
      activeAreaName.value = area?.wareName || area?.wareNo || ''
    }

    const selectLocation = (loc) => {
      const area = areas.value.find((a) => a.id === activeAreaId.value) || null
      selectedArea.value = area
      selectedLocation.value = loc
    }

    const confirmSelector = () => {
      if (!selectedLocationId.value) return
      const idx = activePalletIndex.value
      if (idx >= 0 && pallets.value?.[idx]) {
        const area = selectedArea.value
        const loc = selectedLocation.value
        pallets.value[idx].selectedArea = area
        pallets.value[idx].selectedLocation = loc
        const areaName = area?.wareName
        const locName = loc?.wareName
        pallets.value[idx].selectedLocationText = areaName && locName ? `${areaName} / ${locName}` : ''
      }
      closeSelector()
    }

    const handleSubmitAll = async () => {
      if (submitting.value) return
      if (!canSubmit.value) return

      submitting.value = true
      try {
        await unloadWareIn({
          id: wareInId.value,
          wareInDetailRecordVOList: pallets.value.map((p) => ({
            palletCode: p.palletCode,
            palletId: p.palletId,
            wareId: p.selectedLocation?.id,
            wareName: p.selectedLocation?.wareName,
            storeId: storeId.value
          }))
        })
        toast.success('操作成功')
        router.replace({
          path: '/warein-putaway',
          query: {
            refresh: String(Date.now())
          }
        })
      } catch (e) {
        toast.error(e?.message || '上架失败')
      } finally {
        submitting.value = false
      }
    }

    const fetchDetail = async () => {
      if (!wareInId.value) return
      loading.value = true
      errorMsg.value = ''
      try {
        const res = await getWareInPutawayDetail(wareInId.value)
        const data = res?.data
        const details = Array.isArray(data?.wareInDetailList) ? data.wareInDetailList : []

        const next = []
        const seen = new Set()
        details.forEach((d) => {
          const list = Array.isArray(d?.wareInDetailRecordVOList)
            ? d.wareInDetailRecordVOList
            : (Array.isArray(d?.recordVOList) ? d.recordVOList : [])

          list.forEach((r) => {
            if (!r?.palletCode) return
            const key = String(r.palletId || r.palletCode)
            if (seen.has(key)) return
            seen.add(key)
            next.push({
              palletCode: r.palletCode,
              palletId: r.palletId,
              selectedArea: null,
              selectedLocation: null,
              selectedLocationText: ''
            })
          })
        })

        pallets.value = next
      } catch (e) {
        errorMsg.value = e?.message || '加载失败'
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      wareInId.value = String(route.query.wareInId || '')
      storeId.value = String(route.query.storeId || '')
      supplierId.value = String(route.query.supplierId || '')
      fetchDetail()
    })

    return {
      loading,
      errorMsg,
      pallets,
      submitting,
      canSubmit,
      handleBack,
      openLocationSelector,
      showSelector,
      closeSelector,
      selectorLoading,
      selectorError,
      warehouseName,
      areas,
      activeAreaId,
      activeAreaName,
      activeLocations,
      selectedLocationId,
      selectArea,
      selectLocation,
      confirmSelector,
      handleSubmitAll
    }
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 74px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(64px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  border: none;
  background: transparent;
  padding: 6px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-right {
  width: 40px;
  height: 40px;
}

.content {
  padding: 10px 12px;
  margin-top: calc(64px + env(safe-area-inset-top));
}

.status {
  padding: 16px;
  background: var(--card-background);
  border-radius: 12px;
  color: var(--text-secondary);
}

.section {
  background: var(--card-background);
  border-radius: 12px;
  overflow: hidden;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px;
}

.row-btn {
  width: 100%;
  border: none;
  background: transparent;
}

.label {
  font-size: 13px;
  color: var(--text-secondary);
}

.value {
  font-size: 13px;
  color: var(--text-primary);
}

.value-placeholder {
  color: var(--text-light);
}

.arrow {
  margin-left: 6px;
  color: var(--text-light);
  font-size: 18px;
}

.divider {
  height: 1px;
  background: var(--border-color);
}

.bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  background: var(--card-background);
  border-top: 1px solid var(--border-color);
}

.bottom-btn {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 0px;
  background: var(--primary-color);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
}

.bottom-btn:disabled {
  opacity: 0.45;
}

.selector {
  position: fixed;
  inset: 0;
  z-index: 50;
}

.selector-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
}

.selector-panel {
  position: absolute;
  inset: 0;
  background: var(--card-background);
  display: flex;
  flex-direction: column;
}

.selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
}

.selector-back {
  border: none;
  background: transparent;
  width: 40px;
  height: 40px;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.selector-back-icon {
  width: 22px;
  height: 22px;
}

.selector-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--header-text);
}

.selector-right {
  width: 40px;
  height: 40px;
}

.selector-warehouse {
  padding: 10px 16px;
  color: var(--text-secondary);
  font-size: 13px;
}

.selector-loading,
.selector-error {
  padding: 16px;
  color: var(--text-secondary);
}

.selector-body {
  flex: 1;
  display: flex;
  min-height: 0;
}

.areas {
  width: 120px;
  background: var(--background-color);
  overflow: auto;
}

.area-item {
  width: 100%;
  border: none;
  background: transparent;
  padding: 14px 12px;
  text-align: left;
  color: var(--text-primary);
  font-size: 14px;
}

.area-item.active {
  background: var(--card-background);
  border-left: 3px solid var(--primary-color);
  color: var(--primary-color);
  font-weight: 600;
}

.locations {
  flex: 1;
  padding: 12px;
  overflow: auto;
}

.locations-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.location-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.location-card {
  border: 1px solid var(--border-color);
  background: var(--card-background);
  border-radius: 8px;
  padding: 12px;
  text-align: left;
}

.location-card.selected {
  border-color: var(--primary-color);
}

.location-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.location-sub {
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

.selector-footer {
  padding: 10px 12px;
  border-top: 1px solid var(--border-color);
}

.selector-confirm {
  width: 100%;
  height: 44px;
  border: none;
  background: var(--primary-color);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
}

.selector-confirm:disabled {
  opacity: 0.45;
}

 .action-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
 }

 .action-overlay-panel {
  background: rgba(255, 255, 255, 0.96);
  border-radius: 14px;
  padding: 18px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  min-width: 140px;
 }

 .action-overlay-text {
 font-size: 14px;
 color: var(--text-primary);
 font-weight: 600;
}

.action-spinner {
 width: 30px;
 height: 30px;
 border-radius: 50%;
 border: 3px solid rgba(35, 177, 192, 0.2);
 border-top-color: var(--primary-color);
 animation: spin 0.9s linear infinite;
}

 @keyframes spin {
  to {
    transform: rotate(360deg);
  }
 }
</style>
